import React, { useState, useEffect, useMemo, useCallback, useRef, lazy, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Calendar, MapPin, Clock, Shield, 
  User, CheckCircle, Users, GraduationCap,
  Building, ArrowLeft, Phone, Mail, Share2
} from 'lucide-react';
import useFetchClinicianPublicProfile from '../hooks/Clinic/useFetchClinicianBySlug';
import Preloader from '../components/ui/Preloader';

// Lazy load the appointment form
const AppointmentRequestForm = lazy(() => 
  import('../components/appointments/AppointmentRequestForm')
);

// Import contact modals
import { EmailModal, ShareModal } from '../components/modals/ContactModals';

// --- CONSTANTS & CONFIGURATION ---
const DEFAULT_CLINICIAN_DATA = {
  displayName: 'Clinician',
  title: 'Mental Health Professional',
  location: 'Brooklyn, NY',
  experience: '15 years experience'
};

/** API may return `location` as a string or { id_location, name, postal_code } */
function formatLocationPart(loc: unknown): string {
  if (loc == null) return '';
  if (typeof loc === 'string') return loc.trim();
  if (typeof loc === 'object' && loc !== null) {
    const o = loc as Record<string, unknown>;
    const name = typeof o.name === 'string' ? o.name.trim() : '';
    const postal = typeof o.postal_code === 'string' ? o.postal_code.trim() : '';
    return [name, postal].filter(Boolean).join(', ');
  }
  return '';
}

function getClinicianLocationDisplay(clinician: {
  location?: unknown;
  locations?: unknown;
  address?: { city?: string; state?: string; zip?: string };
} | null | undefined): string {
  if (!clinician) return DEFAULT_CLINICIAN_DATA.location;

  const { location, locations, address } = clinician;

  if (location != null) {
    if (Array.isArray(location)) {
      const line = location.map(formatLocationPart).filter(Boolean).join(' · ');
      if (line) return line;
    } else {
      const line = formatLocationPart(location);
      if (line) return line;
    }
  }

  if (Array.isArray(locations) && locations.length > 0) {
    const line = locations.map(formatLocationPart).filter(Boolean).join(' · ');
    if (line) return line;
  }

  if (address && (address.city || address.state || address.zip)) {
    return [address.city, address.state, address.zip].filter(Boolean).join(', ');
  }

  return DEFAULT_CLINICIAN_DATA.location;
}

/** Specialties may be strings or { name } objects from API */
function normalizeSpecialtyEntry(s: unknown): string {
  if (s == null) return '';
  if (typeof s === 'string') return s;
  if (typeof s === 'object' && s !== null) {
    const o = s as Record<string, unknown>;
    if (typeof o.name === 'string') return o.name;
    if (typeof o.label === 'string') return o.label;
  }
  return '';
}

const TAB_CONFIG = {
  overview: 'Overview',
  specialties: 'Specialties & Approach',
  credentials: 'Credentials',
  fees: 'Fees & Insurance'
};

// --- STYLING CONSTANTS for Soft UI & Glassmorphism ---
const glassCardStyle = "bg-white/60 backdrop-blur-xl border border-white/30 shadow-lg rounded-2xl p-6 transition-all duration-300";
const softButtonStyle = "w-full text-sm font-semibold py-3 px-4 rounded-xl shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-300 flex items-center justify-center gap-2";
const primaryButtonStyle = `${softButtonStyle} bg-gradient-to-br from-blue-500 to-blue-600 text-white focus:ring-blue-500`;
const secondaryButtonStyle = `${softButtonStyle} bg-white/80 text-blue-600 border border-white/50 hover:bg-white focus:ring-blue-500`;


// --- CUSTOM HOOKS (Unchanged Logic) ---
const useClinicianInfo = (selectedClinician) => {
  return useMemo(() => {
    if (!selectedClinician) return null;
    const displayName = selectedClinician.fullName || 
      `${selectedClinician.firstName || ''} ${selectedClinician.lastName || ''}`.trim() || 
      DEFAULT_CLINICIAN_DATA.displayName;
    const specialties =
      (selectedClinician.specialties?.filter(Boolean) || [])
        .map(normalizeSpecialtyEntry)
        .filter(Boolean);
    const isVerified = selectedClinician.status === 'active';

    return {
      displayName,
      specialties,
      isVerified,
      clinicianId: selectedClinician.clinicianId,
    };
  }, [selectedClinician]);
};

const useSEOData = (clinicianInfo, selectedClinician) => {
  return useMemo(() => {
    if (!clinicianInfo || !selectedClinician) {
      return {
        title: 'Clinician Profile | TherapistDirect',
        description: 'Find qualified mental health professionals in your area.',
        url: window.location.href,
      };
    }
    const specialtiesText = clinicianInfo.specialties?.length > 0 
      ? `Specializing in ${clinicianInfo.specialties.slice(0, 3).join(', ')}` 
      : 'Mental Health Professional';
    const title = `${clinicianInfo.displayName}, ${selectedClinician.title || 'Professional'} | TherapistDirect`;
    const description = `${clinicianInfo.displayName} is a ${specialtiesText}. Book your appointment today.`;
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Person",
      "name": clinicianInfo.displayName,
      "jobTitle": selectedClinician.title || "Mental Health Professional",
      "description": description,
      "image": selectedClinician.profile?.profileImage || "/images/default-clinician.jpg",
      "url": window.location.href,
    };
    return { title, description, url: window.location.href, structuredData };
  }, [clinicianInfo, selectedClinician]);
};

// --- REFACTORED & STYLED COMPONENTS ---

const TabNavigation = React.memo(({ activeTab, onTabChange }) => (
  <nav className="flex items-center justify-center space-x-2 sm:space-x-4 p-1 bg-white/70 backdrop-blur-sm border border-white/40 rounded-full shadow-inner">
    {Object.entries(TAB_CONFIG).map(([key, label]) => (
      <button 
        key={key}
        onClick={() => onTabChange(key)}
        className={`relative py-2 px-4 text-sm font-semibold transition-colors duration-300 rounded-full ${
          activeTab === key 
            ? 'text-white' 
            : 'text-slate-600 hover:text-slate-900'
        }`}
      >
        {activeTab === key && (
          <span className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full shadow-md z-0"></span>
        )}
        <span className="relative z-10">{label}</span>
      </button>
    ))}
  </nav>
));

const SpecialtiesList = React.memo(({ specialties, maxDisplay = 6 }) => {
  if (!specialties || specialties.length === 0) {
    return <div className="text-slate-500">No specialties listed.</div>;
  }
  return (
    <div className="flex flex-wrap gap-2">
      {specialties.slice(0, maxDisplay).map((specialty, index) => (
        <span key={`${specialty}-${index}`} className="bg-white/70 text-purple-800 px-3 py-1 rounded-full text-sm font-medium border border-white/50">
          {specialty}
        </span>
      ))}
      {specialties.length > maxDisplay && (
        <span className="text-blue-600 text-sm font-medium cursor-pointer hover:underline">
          +{specialties.length - maxDisplay} more
        </span>
      )}
    </div>
  );
});

const LicenseCard = React.memo(({ license }) => {
  const licenseData = typeof license === 'string' ? { number: license } : license;
  const isExpired = licenseData.expirationDate && new Date(licenseData.expirationDate) < new Date();
  
  return (
    <div className={`${glassCardStyle} p-4`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <Shield className={`w-6 h-6 ${isExpired ? 'text-red-500' : 'text-green-500'}`} />
          <h3 className="font-semibold text-slate-800">{licenseData.type || 'Professional License'}</h3>
        </div>
        <div className={`px-2.5 py-1 rounded-full text-xs font-bold ${isExpired ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
          {isExpired ? 'Expired' : 'Active'}
        </div>
      </div>
      <div className="space-y-2 text-sm">
        <div className="flex justify-between"><span className="text-slate-500">License #:</span><span className="font-mono text-slate-700">{licenseData.number}</span></div>
        {licenseData.state && <div className="flex justify-between"><span className="text-slate-500">State:</span><span className="text-slate-700">{licenseData.state}</span></div>}
        {licenseData.expirationDate && <div className="flex justify-between"><span className="text-slate-500">Expires:</span><span className={`font-medium ${isExpired ? 'text-red-600' : 'text-slate-700'}`}>{new Date(licenseData.expirationDate).toLocaleDateString()}</span></div>}
      </div>
    </div>
  );
});

const ActionSidebar = React.memo(({ 
  clinicianName, 
  onTrackButton,
  onOpenEmailModal,
  onOpenShareModal
}) => (
  <div className="space-y-6">
    <div className={glassCardStyle}>
      <h3 className="text-lg font-bold text-slate-800 mb-2">Take the first step</h3>
      <p className="text-sm text-slate-600 mb-4">Contact {clinicianName || 'this clinician'} to schedule a consultation.</p>
      <div className="space-y-3">
        <button 
          onClick={() => {
            onTrackButton('send_message', { messageType: 'initial_contact' });
            onOpenEmailModal();
          }} 
          className={primaryButtonStyle}
        >
          <Mail className="w-4 h-4" />Send a message
        </button>
      </div>
    </div>
    
    <div className={glassCardStyle}>
      <h3 className="text-lg font-bold text-slate-800 mb-4">Quick Contact</h3>
      <div className="space-y-3">
        <button 
          onClick={() => {
            onTrackButton('share_profile', { shareMethod: 'share_button' });
            onOpenShareModal();
          }} 
          className="w-full text-sm font-semibold py-3 px-4 rounded-xl shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-300 flex items-center justify-center gap-2 bg-white/80 text-purple-600 border border-white/50 hover:bg-white focus:ring-purple-500"
        >
          <Share2 className="w-4 h-4" />Share Profile
        </button>
      </div>
    </div>
    
    <div className={glassCardStyle}>
      <h3 className="text-lg font-bold text-slate-800 mb-4">Session Details</h3>
      <div className="space-y-3 text-sm">
        <div className="flex items-center gap-3"><Clock className="w-5 h-5 text-green-600 flex-shrink-0" /><span className="text-slate-700 font-medium">$120-150 per session</span></div>
        <div className="flex items-center gap-3"><Users className="w-5 h-5 text-blue-600 flex-shrink-0" /><span className="text-slate-700">Sees individuals & couples</span></div>
        <div className="flex items-center gap-3"><Calendar className="w-5 h-5 text-purple-600 flex-shrink-0" /><span className="text-slate-700">In-person & online sessions</span></div>
      </div>
    </div>
  </div>
));

// --- MAIN COMPONENT ---
const ClinicianPublicPage = () => {
  const { clinicianId } = useParams();
  const navigate = useNavigate();
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Modal states
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  
  const { selectedClinician, isLoading, error, FetchClinicianPublicProfile } = useFetchClinicianPublicProfile();

  // Ref so tracking callbacks can read the latest clinician without being deps
  const selectedClinicianRef = useRef(selectedClinician);
  const pageViewTrackedRef = useRef(false);
  useEffect(() => { selectedClinicianRef.current = selectedClinician; }, [selectedClinician]);

  const clinicianInfo = useClinicianInfo(selectedClinician);
  const seoData = useSEOData(clinicianInfo, selectedClinician);

  // Function to track button clicks and send connection request data
  // This sends comprehensive tracking data to the backend for analytics
  const trackButtonInteraction = useCallback(async (buttonType, additionalData = {}) => {
    if (clinicianId) {
      try {
        const trackingData = {
          clinicianId: selectedClinicianRef.current?._id || selectedClinicianRef.current?.clinicianId,
          slug: clinicianId,
          practiceId: selectedClinicianRef.current?.practice_id || selectedClinicianRef.current?.id_practice,
          action: buttonType,
          actionType: buttonType,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          referrer: document.referrer || '',
          pageUrl: window.location.href,
          viewport: {
            width: window.innerWidth,
            height: window.innerHeight
          },
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          language: navigator.language,
          sessionId: sessionStorage.getItem('sessionId') || `session_${Date.now()}`,
          pageLoadTime: performance.now(),
          clinicianName: clinicianInfo?.displayName || 'Unknown',
          clinicianSpecialties: clinicianInfo?.specialties || [],
          ...additionalData
        };
        
        
        const response = await fetch('https://ycx2wdyct1.execute-api.us-east-2.amazonaws.com/dev/profile/connection-request', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(trackingData)
        });
        
        if (response.ok) {
        } else {
          console.error(`Failed to track ${buttonType} button click:`, response.status);
        }
      } catch (error) {
        console.error(`Error tracking ${buttonType} button click:`, error);
      }
    }
  }, [clinicianId, clinicianInfo]);

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    // Track tab interaction with enhanced data
    trackButtonInteraction('tab_change', { 
      tabName: tab,
      action: 'navigation',
      section: 'profile_content'
    });
  }, [trackButtonInteraction]);
  
  const handleBackToSearch = useCallback(() => {
    // Track back button click
    trackButtonInteraction('go_back_to_search', { 
      action: 'navigation',
      destination: 'search_results'
    });
    navigate(-1);
  }, [navigate, trackButtonInteraction]);

  const toggleAppointmentForm = useCallback(() => {
    setShowAppointmentForm(prev => !prev);
    // Track appointment form interaction with enhanced data
    trackButtonInteraction('appointment_form_toggle', { 
      isOpen: !showAppointmentForm,
      action: 'form_interaction',
      formType: 'appointment_request'
    });
  }, [showAppointmentForm, trackButtonInteraction]);

  // Modal handler functions
  const openEmailModal = useCallback(() => {
    setShowEmailModal(true);
  }, []);

  const openShareModal = useCallback(() => {
    setShowShareModal(true);
  }, []);
  
  
  const pageView = useCallback(async () => {
    if (clinicianId) {
      try {
        const trackingData = {
          clinicianId: selectedClinicianRef.current?._id || selectedClinicianRef.current?.clinicianId,
          slug: clinicianId,
          practiceId: selectedClinicianRef.current?.practice_id || selectedClinicianRef.current?.id_practice,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          referrer: document.referrer || '',
          pageUrl: window.location.href,
          viewport: {
            width: window.innerWidth,
            height: window.innerHeight
          },
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          language: navigator.language,
          activity_type: 'profile_view',
          action_type: 'profile_click',
          sessionId: sessionStorage.getItem('sessionId') || `session_${Date.now()}`,
          pageLoadTime: performance.now()
        };
        
        const response = await fetch('https://ycx2wdyct1.execute-api.us-east-2.amazonaws.com/dev/profile/views', {
          method: 'POST', 
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(trackingData)
        });
        
        if (response.ok) {
          // Store session ID for future requests
          if (!sessionStorage.getItem('sessionId')) {
            sessionStorage.setItem('sessionId', trackingData.sessionId);
          }
        } else {
          console.warn('Failed to track page view:', response.status);
        }
      } catch (error) {
        console.error('Error tracking page view:', error);
      }
    }
  }, [clinicianId]);

  useEffect(() => {
    if (clinicianId) {
      pageViewTrackedRef.current = false;
      FetchClinicianPublicProfile(clinicianId);
    }
  }, [clinicianId, FetchClinicianPublicProfile]);

  // Track page view only after clinician data has loaded (so _id is available)
  useEffect(() => {
    if (selectedClinician && !pageViewTrackedRef.current) {
      pageViewTrackedRef.current = true;
      pageView();
    }
  }, [selectedClinician, pageView]);

  if (isLoading) return <Preloader />;

  if (error || !selectedClinician) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center text-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Clinician Not Found</h1>
          <p className="text-slate-600">This profile could not be loaded.</p>
          <button onClick={handleBackToSearch} className="mt-4 bg-blue-600 text-white px-5 py-2.5 rounded-lg hover:bg-blue-700">Go Back</button>
        </div>
      </div>
    );
  }
  
  const initials = clinicianInfo?.displayName?.split(' ').map(n => n[0]).join('').toUpperCase() || 'CL';

  return (
    <>
      <Helmet>
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <link rel="canonical" href={seoData.url} />
        {seoData.structuredData && <script type="application/ld+json">{JSON.stringify(seoData.structuredData)}</script>}
      </Helmet>

      <div className="min-h-screen bg-slate-50 font-sans">
        <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
          <button onClick={handleBackToSearch} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Back to search</span>
          </button>
          
          {/* --- Glassmorphic Header Card --- */}
          <header className={`${glassCardStyle} mb-8`}>
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="w-24 h-24 bg-gradient-to-br from-purple-400 to-indigo-500 rounded-xl flex items-center justify-center text-white font-bold text-3xl flex-shrink-0 shadow-lg">
                {initials}
              </div>
              <div className="flex-grow">
                {clinicianInfo?.isVerified && (
                  <div className="flex items-center gap-1.5 text-green-600 text-sm font-semibold mb-2">
                    <CheckCircle className="w-4 h-4" />
                    <span>Verified Professional</span>
                  </div>
                )}
                <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">{selectedClinician?.fullName}</h1>
                <p className="text-lg text-slate-600 font-medium">{selectedClinician?.title}</p>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-3 text-sm text-slate-600">
                  <div className="flex items-center gap-1.5"><GraduationCap className="w-4 h-4" /><span>{DEFAULT_CLINICIAN_DATA.experience}</span></div>
                  <div className="flex items-center gap-1.5"><MapPin className="w-4 h-4" /><span>{getClinicianLocationDisplay(selectedClinician)}</span></div>
                  {selectedClinician?.phone && (
                    <div className="flex items-center gap-1.5"><Phone className="w-4 h-4" /><span>{selectedClinician.phone}</span></div>
                  )}
                </div>
              </div>
              <div className="w-full md:w-auto flex flex-col sm:flex-row md:flex-col gap-3 flex-shrink-0 pt-2">
                <button 
                  onClick={() => {
                    trackButtonInteraction('share_profile_header', { shareMethod: 'header_button' });
                    openShareModal();
                  }} 
                  className={secondaryButtonStyle}
                >
                  <Share2 className="w-4 h-4" /> Share Profile
                </button>
              </div>
            </div>
          </header>

          {/* --- Tab Navigation --- */}
          <div className="flex justify-center mb-8">
            <TabNavigation activeTab={activeTab} onTabChange={handleTabChange} />
          </div>

          {/* --- Content Area --- */}
          <main className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              {activeTab === 'overview' && (
                <>
                  <div className={glassCardStyle}>
                    <h2 className="text-xl font-bold text-slate-800 mb-4">About</h2>
                    <p className="text-slate-700 leading-relaxed">Thank you for visiting my site. Therapy offers a unique opportunity to get to know ourselves, and provides a safe place to think about the ways we can make things better...</p>
                  </div>
                  <div className={glassCardStyle}>
                    <h2 className="text-xl font-bold text-slate-800 mb-4">Top Specialties</h2>
                    <SpecialtiesList specialties={clinicianInfo?.specialties} />
                  </div>
                   <div className={glassCardStyle}>
                      <h2 className="text-xl font-bold text-slate-800 mb-6">Client Focus</h2>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                        <div className="flex items-center gap-3"><Users className="w-6 h-6 text-blue-600" /><div><h3 className="font-semibold text-slate-800">Age</h3><p className="text-sm text-slate-600">Adults, Seniors (65+)</p></div></div>
                        <div className="flex items-center gap-3"><User className="w-6 h-6 text-purple-600" /><div><h3 className="font-semibold text-slate-800">Session Types</h3><p className="text-sm text-slate-600">Individuals, Couples</p></div></div>
                        <div className="flex items-center gap-3"><Building className="w-6 h-6 text-green-600" /><div><h3 className="font-semibold text-slate-800">Communities</h3><p className="text-sm text-slate-600">LGBTQ+, Veterans</p></div></div>
                      </div>
                    </div>
                </>
              )}
              {activeTab === 'specialties' && (
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className={glassCardStyle}>
                      <h2 className="text-xl font-bold text-slate-800 mb-4">Top Specialties</h2>
                      <div className="space-y-2 text-slate-700">
                        {clinicianInfo?.specialties?.map((s, i) => <div key={i} className="py-2 border-b border-white/50 last:border-b-0">{s}</div>) || "N/A"}
                      </div>
                    </div>
                    <div className={glassCardStyle}>
                      <h2 className="text-xl font-bold text-slate-800 mb-4">Treatment Approaches</h2>
                       <div className="space-y-2 text-slate-700">
                        {['CBT', 'DBT', 'Mindfulness-Based MBCT', 'Psychodynamic'].map((a, i) => <div key={i} className="py-2 border-b border-white/50 last:border-b-0">{a}</div>)}
                      </div>
                    </div>
                 </div>
              )}
              {activeTab === 'credentials' && (
                <div className={glassCardStyle}>
                  <h2 className="text-xl font-bold text-slate-800 mb-4">Licenses & Credentials</h2>
                  {selectedClinician.licenses?.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {selectedClinician.licenses.map((license, index) => <LicenseCard key={index} license={license} />)}
                    </div>
                  ) : <div className="text-slate-500">No license information available.</div>}
                </div>
              )}
              {activeTab === 'fees' && (
                <div className={glassCardStyle}>
                  <h2 className="text-xl font-bold text-slate-800 mb-4">Insurance & Payment</h2>
                  <div className="space-y-4">
                    <div><h3 className="font-semibold text-slate-800 mb-2">Accepted Insurance</h3><p className="text-sm text-slate-600">Aetna, Blue Cross, Cigna, UnitedHealthcare, Medicare, Medicaid</p></div>
                    <div><h3 className="font-semibold text-slate-800 mb-2">Payment Methods</h3><p className="text-sm text-slate-600">Cash, Check, Credit Card, HSA/FSA</p></div>
                  </div>
                </div>
              )}
            </div>
            
            {/* --- Right Sidebar --- */}
            <aside>
              <ActionSidebar 
                clinicianName={clinicianInfo?.displayName} 
                onTrackButton={trackButtonInteraction}
                onOpenEmailModal={openEmailModal}
                onOpenShareModal={openShareModal}
              />
            </aside>
          </main>
        </div>
        
        {/* --- Appointment Form Modal --- */}
        {showAppointmentForm && (
          <Suspense fallback={<div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center text-white">Loading Form...</div>}>
            <AppointmentRequestForm
              clinicianName={clinicianInfo?.displayName || ''}
              onClose={() => setShowAppointmentForm(false)}
            />
          </Suspense>
        )}

        {/* --- Contact Modals --- */}
        <EmailModal
          isOpen={showEmailModal}
          onClose={() => setShowEmailModal(false)}
          clinicianName={clinicianInfo?.displayName || 'this clinician'}
          onTrackButton={trackButtonInteraction}
        />


        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          clinicianName={clinicianInfo?.displayName || 'this clinician'}
          profileUrl={window.location.href}
          onTrackButton={trackButtonInteraction}
        />
      </div>
    </>
  );
};

export default React.memo(ClinicianPublicPage);
